import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiClock, FiCalendar, FiArrowRight } from 'react-icons/fi';
import { BLOG_POSTS } from '../data/blogPosts';
import SEO from '../components/SEO';
import { useSettings } from '../context/SettingsContext';

// Renders **bold** and [text](url) inline markup; internal links stay in-app.
const renderInline = (text, keyBase) => text.split(/(\*\*.*?\*\*|\[[^\]]+\]\([^)]+\))/g).filter(Boolean).map((part, j) => {
  const key = `${keyBase}-${j}`;
  if (part.startsWith('**') && part.endsWith('**')) return <strong key={key} className="text-[#1A1A2E]">{renderInline(part.slice(2, -2), key)}</strong>;
  const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
  if (m) {
    const href = m[2].replace(/^https?:\/\/(www\.)?royalgardenvillas\.in/, '') || '/';
    return href.startsWith('/')
      ? <Link key={key} to={href} className="text-[#F57C20] font-semibold hover:underline">{m[1]}</Link>
      : <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="text-[#F57C20] font-semibold hover:underline">{m[1]}</a>;
  }
  return part;
});

const BlogPost = () => {
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();
  const { slug } = useParams();
  const post = BLOG_POSTS.find(p => p.slug === slug);
  const otherPosts = BLOG_POSTS.filter(p => p.slug !== slug).slice(0, 3);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) return (
    <div className="pt-28 min-h-screen flex items-center justify-center bg-[#F7F8FA]">
      <div className="text-center">
        <p className="text-[#6B7280] mb-4">Blog post not found</p>
        <Link to="/blog" className="text-[#F57C20] font-semibold">Back to Blog</Link>
      </div>
    </div>
  );

  // Convert markdown-style content to HTML-like rendering
  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;
      if (trimmed.startsWith('## ')) return <h2 key={i} className="text-2xl text-[#1A1A2E] mt-10 mb-4 font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{trimmed.slice(3)}</h2>;
      if (trimmed.startsWith('### ')) return <h3 key={i} className="text-lg text-[#1A1A2E] mt-8 mb-3 font-semibold">{trimmed.slice(4)}</h3>;
      if (trimmed.startsWith('- ')) return <li key={i} className="text-[#4B5563] text-sm leading-relaxed ml-4 mb-1">{renderInline(trimmed.slice(2), i)}</li>;
      if (trimmed.startsWith('| ') && trimmed.includes('|')) {
        const cells = trimmed.split('|').filter(c => c.trim()).map(c => c.trim());
        if (cells.every(c => c.match(/^[-]+$/))) return null; // separator row
        const isHeader = i > 0 && content.split('\n')[i + 1]?.trim().startsWith('|') && content.split('\n')[i + 1]?.includes('---');
        return (
          <tr key={i} className={isHeader ? 'bg-[#1B2432] text-white' : 'border-b border-gray-100 hover:bg-[#F57C20]/5'}>
            {cells.map((cell, j) => isHeader
              ? <th key={j} className="px-3 py-2 text-xs font-semibold text-left">{cell}</th>
              : <td key={j} className="px-3 py-2 text-xs text-[#4B5563]">{renderInline(cell, `${i}-${j}`)}</td>
            )}
          </tr>
        );
      }
      if (trimmed.match(/^\d+\.\s/)) {
        const text = trimmed.replace(/^\d+\.\s/, '');
        return <li key={i} className="text-[#4B5563] text-sm leading-relaxed ml-4 mb-1 list-decimal">{renderInline(text, i)}</li>;
      }
      return <p key={i} className="text-[#4B5563] text-sm leading-relaxed mb-3">{renderInline(trimmed, i)}</p>;
    }).filter(Boolean);
  };

  // Wrap table rows
  const contentElements = renderContent(nightDiscountEnabled ? post.content.replace(/15%/g, `${nightDiscountPct}%`)
    : post.content.split('\n').filter((l) => !/\d+% off|% OFF|Stay Longer, Pay Less|Save \d+%/i.test(l)).join('\n'));
  const wrapped = [];
  let tableRows = [];
  contentElements.forEach((el, i) => {
    if (el?.type === 'tr') {
      tableRows.push(el);
    } else {
      if (tableRows.length > 0) {
        wrapped.push(
          <div key={`table-${i}`} className="overflow-x-auto rounded-xl border border-gray-200 my-6">
            <table className="w-full text-sm"><tbody>{tableRows}</tbody></table>
          </div>
        );
        tableRows = [];
      }
      wrapped.push(el);
    }
  });
  if (tableRows.length > 0) {
    wrapped.push(
      <div key="table-end" className="overflow-x-auto rounded-xl border border-gray-200 my-6">
        <table className="w-full text-sm"><tbody>{tableRows}</tbody></table>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="pt-28 bg-[#F7F8FA]">
      <SEO
        title={`${post.title} | Royal Garden Villas Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: post.title, url: `/blog/${post.slug}` },
        ]}
      />
      {/* Article Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.excerpt,
        "image": post.image,
        "author": { "@type": "Organization", "name": "Royal Garden Villas", "url": "https://royalgardenvillas.in" },
        "publisher": { "@type": "Organization", "name": "Royal Garden Villas" },
        "datePublished": post.isoDate || "2026-04-14",
        "dateModified": post.isoDate || "2026-10-30",
        "mainEntityOfPage": `https://royalgardenvillas.in/blog/${post.slug}`
      })}} />

      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E] via-[#1A1A2E]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-4xl mx-auto">
          <span className="text-[#F57C20] text-xs font-semibold tracking-wider uppercase">{post.category}</span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl text-white mt-2 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{post.title}</h1>
          <div className="flex items-center gap-4 mt-3 text-white/60 text-xs">
            <span className="flex items-center gap-1"><FiCalendar className="w-3 h-3" /> {post.date}</span>
            <span className="flex items-center gap-1"><FiClock className="w-3 h-3" /> {post.readTime}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <Link to="/blog" className="text-[#F57C20] text-xs font-semibold flex items-center gap-1 mb-8 hover:underline"><FiArrowLeft className="w-3 h-3" /> Back to Blog</Link>
        <article className="bg-white rounded-xl p-6 md:p-10 border border-gray-100 shadow-sm">
          {wrapped}
        </article>

        {/* Author */}
        <div className="mt-8 bg-white rounded-xl p-6 border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#F57C20]/10 flex items-center justify-center text-[#F57C20] font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>RG</div>
          <div>
            <p className="text-[#1A1A2E] text-sm font-semibold">by Founder, Royal Garden Villas</p>
            <p className="text-[#6B7280] text-xs">10+ years hosting families, corporates, and celebrations in Lonavala</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 bg-[#1B2432] rounded-xl p-8 text-center">
          <p className="text-[#F57C20] text-xs tracking-wider uppercase font-semibold mb-2">Ready to Experience It?</p>
          <h3 className="text-white text-xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Book Your Villa Today{nightDiscountEnabled ? ` — Stay Longer, Pay Less: ${nightDiscountPct}% OFF 2nd Night Onwards!` : ''}</h3>
          <Link to="/booking" className="bg-[#F57C20] text-white px-8 py-3 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#E06A10] transition-all">
            Book Now <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Related Posts */}
        {otherPosts.length > 0 && (
          <div className="mt-12">
            <h3 className="text-2xl text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>More from <span className="text-[#F57C20] italic">Our Blog</span></h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherPosts.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all">
                  <div className="h-40 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <span className="text-[#F57C20] text-[10px] font-semibold uppercase">{p.category}</span>
                    <h4 className="text-[#1A1A2E] text-sm font-bold mt-1 line-clamp-2">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPost;
